import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiError } from '../../../../abstract/api-error';
import { isViewableBlobType } from '../../../../core/utils/safe-blob-utils';
import { SupportTicketService } from '../../../../core/data-services/support-ticket.service';
import { OrganizationService } from '../../../../core/data-services/organization.service';
import { DroneService } from '../../../../core/data-services/drone.service';
import { Drone } from '../../../../core/data-models/drone.model';
import { TicketRealtimeService, TicketUpdateEvent, TicketCommentEvent, TicketStatusChangedEvent, TicketAttachmentEvent, TicketAttachmentDeletedEvent } from '../../../../core/services/ticket-realtime.service';
import { WebSocketService, ConnectionState } from '../../../../core/services/websocket.service';
import { WithdrawTicketModalComponent, WithdrawTicketResult } from '../../../../shared/components/withdraw-ticket-modal/withdraw-ticket-modal.component';
import { type SupportTicket } from '../../../../core/data-models/support-ticket.model';
import { type SupportTicketCategory } from '../../../../core/data-models/support-ticket-category.model';
import { type SupportTicketThread } from '../../../../core/data-models/support-ticket-thread.model';
import { SupportTicketAttachment } from '../../../../core/data-models/support-ticket-attachment.model';
import { SUPPORT_TICKET_CATEGORIES } from '../../../../shared/constants/support-ticket-options';
import { environment } from '../../../../../environments/environment';
import { formatPhoneNumber } from '../../../../shared/utils/format.utils';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';
import { openExternal } from '../../../../core/utils/external-link-utils';

/**
 * Activity item for the timeline - combines threads, status changes, attachments, and system events
 */
interface ActivityItem {
  id: string;
  type: 'thread' | 'status_change' | 'attachment' | 'system';
  content: string;
  author: string;
  authorType: 'user' | 'agent' | 'system' | 'contact';
  createdAt: string;
  direction?: 'inbound' | 'outbound' | null;
  channel?: string | null;
  oldStatus?: string;
  newStatus?: string;
  attachmentAction?: 'added' | 'removed';
  fileName?: string;
}

// Extended interface for view mode with additional UI-specific data
interface SupportTicketView extends SupportTicket {
  attachments: SupportTicketAttachment[];
  threads: SupportTicketThread[];
  activity: ActivityItem[];
}

@Component({
  selector: 'app-support-ticket-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, WithdrawTicketModalComponent, MaxLengthDirective, CharCounterDirective],
  templateUrl: './support-ticket-form.component.html',
  styleUrls: ['./support-ticket-form.component.scss']
})
export class SupportTicketFormComponent implements OnInit, OnDestroy {
  @ViewChild('withdrawModal') withdrawModal!: WithdrawTicketModalComponent;

  private destroy$ = new Subject<void>();

  form!: FormGroup;
  isViewMode = false;
  ticketId: string | null = null;
  isLoading = false;
  isSaving = false;
  isUpdatingStatus = false;

  // Technical support phone
  technicalSupportPhone = environment.technicalSupportPhone;
  formattedSupportPhone = formatPhoneNumber(environment.technicalSupportPhone);

  // Organization context
  organizationId: string | null = null;

  // Current ticket data (for view mode)
  ticket: SupportTicketView | null = null;

  // Attachments for new ticket
  pendingAttachments: File[] = [];
  uploadProgress: number = 0;
  isUploading = false;

  // New comment for view mode
  newComment = '';
  isAddingComment = false;

  // Category options from model
  categories = SUPPORT_TICKET_CATEGORIES.map(c => c.value);

  // Drones assigned to the organization
  drones: Drone[] = [];
  private droneService = inject(DroneService);
  isDronesLoading = false;

  // Real-time connection status
  connectionState: ConnectionState = 'disconnected';
  hasRealtimeUpdate = false;

  // Activity collapse settings
  showOlderMessages = false;
  recentMessageCount = 3; // Number of messages to show when collapsed

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private ticketService: SupportTicketService,
    private organizationService: OrganizationService,
    private ticketRealtime: TicketRealtimeService,
    private websocketService: WebSocketService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initialize();
    this.subscribeToRealtimeUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.form = this.fb.group({
      subject: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(200)]],
      category: ['', Validators.required],
      droneId: [''],
      description: ['', [Validators.required, Validators.minLength(20)]]
    });
  }

  private async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      // Get current organization
      const org = await this.organizationService.getMyOrganization();
      if (org) {
        this.organizationId = org.id;
        // Load drones assigned to this organization
        await this.loadDrones();
      }

      // Check mode and load ticket if needed
      this.ticketId = this.route.snapshot.paramMap.get('id');

      if (this.ticketId) {
        // View mode if we have a ticket ID
        this.isViewMode = true;
        await this.loadTicket(this.ticketId);
      }
    } catch (error) {
      console.error('Error initializing:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadDrones(): Promise<void> {
    this.isDronesLoading = true;
    try {
      this.drones = (await this.droneService.getOrganizationDrones()).filter(drone => drone.ownership === 'adn');
    } catch (error) {
      console.error('Error loading drones:', error);
      this.drones = [];
    } finally {
      this.isDronesLoading = false;
    }
  }

  private async loadTicket(id: string): Promise<void> {
    try {
      const ticket = await this.ticketService.getTicket(id);
      if (ticket) {
        // Load threads and attachments in parallel
        const [threads, attachments] = await Promise.all([
          this.ticketService.getTicketThreads(id).catch(() => []),
          this.ticketService.getTicketAttachments(id).catch(() => [])
        ]);

        // Build activity timeline from threads
        const activity = this.buildActivityTimeline(ticket, threads);

        // Extend with UI-specific fields
        this.ticket = {
          ...ticket,
          attachments,
          threads,
          activity
        };

      } else {
        // Ticket not found
        this.router.navigate(['/maintenance/support-tickets']);
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
      this.router.navigate(['/maintenance/support-tickets']);
    }
  }

  /**
   * Build the activity timeline from threads and ticket events
   */
  private buildActivityTimeline(ticket: SupportTicket, threads: SupportTicketThread[]): ActivityItem[] {
    const activity: ActivityItem[] = [];

    // Add ticket creation event
    activity.push({
      id: 'created',
      type: 'system',
      content: 'Ticket created',
      author: ticket.createdByName || 'Unknown',
      authorType: 'system',
      createdAt: isoString(ticket.createdAt)
    });

    // Add threads to activity
    for (const thread of threads) {
      // Map author type from API to UI format
      let authorType: 'user' | 'agent' | 'system' | 'contact' = 'contact';
      if (thread.authorType === 'agent') {
        authorType = 'agent';
      } else if (thread.authorType === 'system') {
        authorType = 'system';
      } else if (thread.direction === 'outbound' && thread.createdByProfileId) {
        // Outbound from a Hub user = "user" (customer)
        authorType = 'user';
      }

      activity.push({
        id: thread.id,
        type: 'thread',
        content: thread.content ?? '',
        author: this.sanitizeAuthorName(thread.authorName, thread.createdByName, authorType),
        authorType,
        createdAt: isoString(thread.createdAt),
        direction: thread.direction,
        channel: thread.channel
      });
    }

    // Sort by creation date
    activity.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    return activity;
  }

  /**
   * Sanitize author name - handles "undefined undefined" and other invalid values
   */
  private sanitizeAuthorName(authorName: string | null | undefined, createdByName: string | null | undefined, authorType: string): string {
    // Check authorName first - reject "undefined undefined" or similar invalid values
    if (authorName && authorName !== 'undefined undefined' && !authorName.includes('undefined')) {
      return authorName;
    }
    // Fall back to createdByName
    if (createdByName && createdByName !== 'undefined undefined' && !createdByName.includes('undefined')) {
      return createdByName;
    }
    // Default based on author type
    if (authorType === 'user' || authorType === 'contact') return 'You';
    if (authorType === 'agent') return 'Support';
    return 'Unknown';
  }

  /**
   * Subscribe to real-time ticket updates via WebSocket
   */
  private subscribeToRealtimeUpdates(): void {
    // Track connection state
    this.websocketService.connectionState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      this.connectionState = state;
      console.log('[TicketForm] WebSocket connection state:', state);
    });

    // Only subscribe to ticket-specific events if we're in view mode
    this.route.paramMap.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.subscribeToTicketEvents(id);
      }
    });
  }

  /**
   * Subscribe to events for a specific ticket
   */
  private subscribeToTicketEvents(ticketId: string): void {
    // Listen for ticket updates
    this.ticketRealtime.onTicketUpdated(ticketId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketForm] Received ticket update:', event);
      this.handleTicketUpdate(event);
    });

    // Listen for new comments
    this.ticketRealtime.onTicketComment(ticketId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketForm] Received new comment:', event);
      this.handleNewComment(event);
    });

    // Listen for status changes
    this.ticketRealtime.onTicketStatusChanged(ticketId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketForm] Received status change:', event);
      this.handleStatusChange(event);
    });

    // Listen for new attachments
    this.ticketRealtime.onTicketAttachment(ticketId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketForm] Received new attachment:', event);
      this.handleNewAttachment(event);
    });

    // Listen for deleted attachments
    this.ticketRealtime.onTicketAttachmentDeleted(ticketId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      console.log('[TicketForm] Received attachment deleted:', event);
      this.handleAttachmentDeleted(event);
    });
  }

  /**
   * Handle ticket update event
   */
  private handleTicketUpdate(event: TicketUpdateEvent): void {
    // Show indicator that ticket was updated
    this.hasRealtimeUpdate = true;

    // Refresh ticket data from server
    this.refreshTicketSilently();
  }

  /**
   * Handle new comment event
   */
  private handleNewComment(event: TicketCommentEvent): void {
    if (!this.ticket) return;

    // Add comment to activity timeline
    const newActivity: ActivityItem = {
      id: event.comment.id,
      type: 'thread',
      content: event.comment.content,
      author: event.comment.author,
      authorType: event.comment.authorType as 'user' | 'agent' | 'system' | 'contact',
      createdAt: event.comment.createdAt
    };

    this.ticket.activity.push(newActivity);
    this.ticket.updatedAt = new Date(event.timestamp);
    this.hasRealtimeUpdate = true;
  }

  /**
   * Handle status change event
   */
  private handleStatusChange(event: TicketStatusChangedEvent): void {
    if (!this.ticket) return;

    // Update ticket status
    const oldStatus = this.ticket.status;
    this.ticket.status = event.newStatus as 'open' | 'in_progress' | 'resolved' | 'closed';
    this.ticket.updatedAt = new Date(event.timestamp);

    // Add status change to activity timeline
    const statusActivity: ActivityItem = {
      id: `status-${Date.now()}`,
      type: 'status_change',
      content: `Status changed from ${this.getStatusText(event.oldStatus)} to ${this.getStatusText(event.newStatus)}`,
      author: event.changedBy || 'Support',
      authorType: 'agent',
      createdAt: event.timestamp,
      oldStatus: event.oldStatus,
      newStatus: event.newStatus
    };

    this.ticket.activity.push(statusActivity);
    this.hasRealtimeUpdate = true;
  }

  /**
   * Handle new attachment event
   */
  private handleNewAttachment(event: TicketAttachmentEvent): void {
    if (!this.ticket) return;

    // Check if attachment already exists (avoid duplicates)
    const exists = this.ticket.attachments.some(a => a.id === event.attachment.id);
    if (exists) return;

    // Add new attachment to the list
    const newAttachment = new SupportTicketAttachment({
      ...event.attachment,
      ticketId: event.ticketId,
      uploadedAt: event.attachment.createdAt,
    });

    this.ticket.attachments.push(newAttachment);
    this.ticket.updatedAt = new Date(event.timestamp);
    this.hasRealtimeUpdate = true;

    // Add attachment activity to timeline
    const attachmentActivity: ActivityItem = {
      id: `attachment-added-${event.attachment.id}`,
      type: 'attachment',
      content: `Attachment added: ${event.attachment.fileName}`,
      author: 'Support',
      authorType: 'system',
      createdAt: event.timestamp,
      attachmentAction: 'added',
      fileName: event.attachment.fileName
    };
    this.ticket.activity.push(attachmentActivity);

    console.log('[TicketForm] Added new attachment to list:', newAttachment.fileName);
  }

  /**
   * Handle attachment deleted event
   */
  private handleAttachmentDeleted(event: TicketAttachmentDeletedEvent): void {
    if (!this.ticket) return;

    // Find and remove the attachment from the list
    const index = this.ticket.attachments.findIndex(a => a.id === event.attachmentId);
    if (index !== -1) {
      const removed = this.ticket.attachments.splice(index, 1);
      const fileName = removed[0]?.fileName || 'Unknown file';
      this.ticket.updatedAt = new Date(event.timestamp);
      this.hasRealtimeUpdate = true;

      // Add attachment removed activity to timeline
      const attachmentActivity: ActivityItem = {
        id: `attachment-removed-${event.attachmentId}`,
        type: 'attachment',
        content: `Attachment removed: ${fileName}`,
        author: 'Support',
        authorType: 'system',
        createdAt: event.timestamp,
        attachmentAction: 'removed',
        fileName
      };
      this.ticket.activity.push(attachmentActivity);

      console.log('[TicketForm] Removed attachment from list:', fileName);
    }
  }

  /**
   * Silently refresh ticket data from server
   */
  private async refreshTicketSilently(): Promise<void> {
    if (!this.ticketId) return;

    try {
      const ticket = await this.ticketService.getTicket(this.ticketId);
      if (ticket && this.ticket) {
        // Reload threads and attachments
        const [threads, attachments] = await Promise.all([
          this.ticketService.getTicketThreads(this.ticketId).catch(() => []),
          this.ticketService.getTicketAttachments(this.ticketId).catch(() => [])
        ]);

        // Rebuild activity timeline
        const activity = this.buildActivityTimeline(ticket, threads);

        this.ticket = {
          ...ticket,
          attachments,
          threads,
          activity
        };
      }
    } catch (error) {
      console.error('Error refreshing ticket:', error);
    }
  }

  /**
   * Dismiss the real-time update indicator
   */
  dismissUpdateNotification(): void {
    this.hasRealtimeUpdate = false;
  }

  // File handling
  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(Array.from(input.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  private addFiles(files: File[]): void {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'video/mp4', 'video/quicktime'];
    const maxSize = 25 * 1024 * 1024; // 25MB

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        alert(`File type not allowed: ${file.name}`);
        continue;
      }
      if (file.size > maxSize) {
        alert(`File too large (max 25MB): ${file.name}`);
        continue;
      }
      if (this.pendingAttachments.length >= 5) {
        alert('Maximum 5 attachments allowed');
        break;
      }
      this.pendingAttachments.push(file);
    }
  }

  removeAttachment(index: number): void {
    this.pendingAttachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) return 'bi-file-image';
    if (fileType.startsWith('video/')) return 'bi-file-play';
    if (fileType === 'application/pdf') return 'bi-file-pdf';
    return 'bi-file-earmark';
  }

  isImageFile(fileType: string): boolean {
    return fileType.startsWith('image/');
  }

  // Comment handling
  async addComment(): Promise<void> {
    if (!this.newComment.trim() || !this.ticket || !this.ticketId) return;

    this.isAddingComment = true;

    try {
      // Submit thread via API
      const thread = await this.ticketService.addTicketThread(this.ticketId, this.newComment);

      // Add to activity timeline
      const newActivity: ActivityItem = {
        id: thread.id,
        type: 'thread',
        content: thread.content ?? '',
        author: this.sanitizeAuthorName(thread.authorName, thread.createdByName, 'user'),
        authorType: 'user',
        createdAt: isoString(thread.createdAt),
        direction: thread.direction,
        channel: thread.channel
      };

      this.ticket.activity.push(newActivity);
      this.ticket.threads.push(thread);
      this.ticket.updatedAt = new Date();
      this.newComment = '';
    } catch (error: unknown) {
      alert(ApiError.fromHttpError(error).errors[0] ?? 'Failed to add reply. Please try again.');
    } finally {
      this.isAddingComment = false;
    }
  }

  // Form submission (create only - no edit)
  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.organizationId) {
      alert('Organization not found. Please try again.');
      return;
    }

    this.isSaving = true;

    try {
      const formValue = this.form.value;

      // Create new ticket
      const ticket = await this.ticketService.createTicket({
        subject: formValue.subject,
        description: formValue.description,
        category: formValue.category || undefined,
        droneId: formValue.droneId || undefined,
        organizationId: this.organizationId
      });

      // Upload attachments if any were added
      if (this.pendingAttachments.length > 0) {
        try {
          const uploadResult = await this.ticketService.uploadAttachments(
            ticket.id,
            this.pendingAttachments
          );

          if (uploadResult.errors && uploadResult.errors.length > 0) {
            console.warn('Some attachments failed to upload:', uploadResult.errors);
            // Still navigate, but warn the user
            alert(`Ticket created. Note: ${uploadResult.errors.length} attachment(s) failed to upload.`);
          }
        } catch (uploadError: any) {
          console.error('Error uploading attachments:', uploadError);
          // Ticket was created, so still navigate but warn the user
          alert('Ticket created, but some attachments failed to upload. You can add them later.');
        }
      }

      this.router.navigate(['/maintenance/support-tickets']);
    } catch (error: unknown) {
      alert(ApiError.fromHttpError(error).errors[0] ?? 'Failed to create ticket. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }

  // Status change methods
  async resolveTicket(): Promise<void> {
    if (!this.ticket || !this.ticketId) return;
    await this.updateTicketStatus('resolved');
  }

  async closeTicket(): Promise<void> {
    if (!this.ticket || !this.ticketId) return;
    await this.updateTicketStatus('closed');
  }

  async reopenTicket(): Promise<void> {
    if (!this.ticket || !this.ticketId) return;
    await this.updateTicketStatus('open');
  }

  // Withdraw ticket methods
  withdrawTicket(): void {
    if (!this.ticket) return;
    this.withdrawModal.open(this.ticket.ticketNumber || this.ticket.id);
  }

  async onWithdrawConfirmed(result: WithdrawTicketResult): Promise<void> {
    if (!this.ticket || !this.ticketId) return;

    this.withdrawModal.setProcessing(true);
    try {
      await this.ticketService.deleteTicket(this.ticketId, result.reason, result.details);
      // Navigate back to the list after withdrawal
      this.router.navigate(['/maintenance/support-tickets']);
    } catch (error: unknown) {
      alert(ApiError.fromHttpError(error).errors[0] ?? 'Failed to withdraw ticket. Please try again.');
      this.withdrawModal.setProcessing(false);
    }
  }

  onWithdrawCancelled(): void {
    // Nothing to clean up
  }

  private async updateTicketStatus(newStatus: 'open' | 'resolved' | 'closed'): Promise<void> {
    if (!this.ticket || !this.ticketId) return;

    this.isUpdatingStatus = true;

    try {
      await this.ticketService.updateTicketStatus(this.ticketId, newStatus);

      // Add status change to activity
      const oldStatus = this.ticket.status;
      this.ticket.status = newStatus;
      this.ticket.updatedAt = new Date();

      this.ticket.activity.push({
        id: `status-${Date.now()}`,
        type: 'status_change',
        content: `Status changed from ${this.getStatusText(oldStatus)} to ${this.getStatusText(newStatus)}`,
        author: 'You',
        authorType: 'user',
        createdAt: new Date().toISOString(),
        oldStatus,
        newStatus
      });
    } catch (error: unknown) {
      alert(ApiError.fromHttpError(error).errors[0] ?? 'Failed to update ticket status. Please try again.');
    } finally {
      this.isUpdatingStatus = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/maintenance/support-tickets']);
  }

  // Validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field || !field.errors) return '';

    if (field.errors['required']) return 'This field is required';
    if (field.errors['minlength']) {
      return `Minimum ${field.errors['minlength'].requiredLength} characters required`;
    }
    if (field.errors['maxlength']) {
      return `Maximum ${field.errors['maxlength'].requiredLength} characters allowed`;
    }
    return 'Invalid value';
  }

  // Status/Priority helpers
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'open': return 'bg-primary';
      case 'in_progress': return 'bg-warning text-dark';
      case 'resolved': return 'bg-success';
      case 'closed': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'open': return 'Open';
      case 'in_progress': return 'In Progress';
      case 'resolved': return 'Resolved';
      case 'closed': return 'Closed';
      default: return status;
    }
  }

  getPriorityBadgeClass(priority: string): string {
    switch (priority) {
      case 'urgent': return 'bg-danger';
      case 'high': return 'bg-warning text-dark';
      case 'medium': return 'bg-info text-dark';
      case 'low': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getPriorityText(priority: string): string {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  }

  // Date formatting
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | Date | null | undefined): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return this.formatDateTime(dateString);
  }

  // Author styling
  getAuthorBadgeClass(authorType: string): string {
    switch (authorType) {
      case 'user': return 'bg-success-subtle text-success';
      case 'agent':
      case 'contact':
        return '';
      case 'system': return 'bg-secondary-subtle text-secondary';
      default: return 'bg-secondary-subtle text-secondary';
    }
  }

  getAuthorLabel(authorType: string): string {
    switch (authorType) {
      case 'user': return 'You';
      case 'agent':
      case 'contact':
        return 'Support';
      case 'system': return 'System';
      default: return '';
    }
  }

  // Activity type icon
  getActivityIcon(activity: ActivityItem): string {
    switch (activity.type) {
      case 'thread': return 'bi-chat-left-text';
      case 'status_change': return 'bi-arrow-left-right';
      case 'attachment': return activity.attachmentAction === 'removed' ? 'bi-paperclip' : 'bi-paperclip';
      case 'system': return 'bi-gear';
      default: return 'bi-info-circle';
    }
  }

  // Get icon for attachment file type
  getAttachmentIcon(attachment: SupportTicketAttachment): string {
    if (attachment.isImage) return 'bi-file-image';
    if (attachment.isPdf) return 'bi-file-pdf';
    if (attachment.fileType?.startsWith('video/')) return 'bi-file-play';
    return attachment.fileIcon || 'bi-file-earmark';
  }

  // Format attachment file size
  formatAttachmentSize(attachment: SupportTicketAttachment): string {
    return attachment.formattedFileSize || this.formatFileSize(attachment.fileSize || 0);
  }

  /**
   * The link for an attachment stored on the CDN (an absolute URL). Zoho Desk attachments point at the hub's
   * authenticated download route instead, which a plain link can't call, so they get no `href` and are
   * downloaded on click.
   */
  getAttachmentUrl(attachment: SupportTicketAttachment): string | null {
    return attachment.downloadUrl?.startsWith('http') ? attachment.downloadUrl : null;
  }

  // Handle attachment click - open images/PDFs in new tab, download others
  async onAttachmentClick(event: Event, attachment: SupportTicketAttachment): Promise<void> {
    event.preventDefault();
    if (!attachment.downloadUrl || !this.ticketId) return;

    // CDN files live on another origin, so the browser can open them directly
    const url = this.getAttachmentUrl(attachment);
    if (url) {
      this.openCdnFile(url, attachment);
      return;
    }

    // Opened synchronously so popup blockers allow it after the await; closed again if the file can't be shown
    const tab = attachment.isImage || attachment.isPdf ? window.open('', '_blank') : null;
    try {
      const blob = await this.ticketService.downloadAttachment(this.ticketId, attachment.id);
      this.showOrSaveBlob(blob, attachment.fileName ?? 'attachment', tab);
    } catch (error: unknown) {
      tab?.close();
      alert(ApiError.fromHttpError(error).errors[0] ?? 'Could not download this attachment.');
    }
  }

  /**
   * A `blob:` URL has the portal's origin, so only allowlisted image/PDF types are opened in a tab; anything
   * else (SVG, HTML, …) could run script with the portal's tokens and is only ever saved, as octet-stream.
   */
  private showOrSaveBlob(blob: Blob, fileName: string, tab: Window | null): void {
    if (tab && isViewableBlobType(blob.type)) {
      tab.opener = null;
      const objectUrl = URL.createObjectURL(blob);
      tab.location.href = objectUrl;
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      return;
    }
    tab?.close();
    const objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
    this.clickDownloadLink(objectUrl, fileName);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }

  /** Images and PDFs on the CDN open in a new tab; other files download. */
  private openCdnFile(url: string, attachment: SupportTicketAttachment): void {
    if (attachment.isImage || attachment.isPdf) {
      openExternal(url);
      return;
    }
    this.clickDownloadLink(url, attachment.fileName ?? '');
  }

  private clickDownloadLink(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ============================================================
  // Activity Collapse/Expand Methods
  // ============================================================

  /**
   * Get the visible activity items based on collapse state
   */
  get visibleActivity(): ActivityItem[] {
    if (!this.ticket?.activity) return [];

    // Filter to only thread items for collapse logic
    const threadItems = this.ticket.activity.filter(a => a.type === 'thread');
    const systemItems = this.ticket.activity.filter(a => a.type !== 'thread');

    if (this.showOlderMessages || threadItems.length <= this.recentMessageCount) {
      return this.ticket.activity;
    }

    // Show system items + last N thread items
    const recentThreads = threadItems.slice(-this.recentMessageCount);
    const recentThreadIds = new Set(recentThreads.map(t => t.id));

    return this.ticket.activity.filter(a =>
      a.type !== 'thread' || recentThreadIds.has(a.id)
    );
  }

  /**
   * Get count of hidden older messages
   */
  get hiddenMessageCount(): number {
    if (!this.ticket?.activity) return 0;
    const threadItems = this.ticket.activity.filter(a => a.type === 'thread');
    return Math.max(0, threadItems.length - this.recentMessageCount);
  }

  /**
   * Toggle showing older messages
   */
  toggleOlderMessages(): void {
    this.showOlderMessages = !this.showOlderMessages;
  }
}

/** An ISO timestamp for the activity timeline ('' when unknown). */
function isoString(date: Date | null): string {
  return date?.toISOString() ?? '';
}
